import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls, BlockControls, AlignmentControl } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	ColorPalette,
	RangeControl,
} from '@wordpress/components';
import ServerSideRender from '@wordpress/server-side-render'; // Displays the PHP output in editor
import './editor.scss';
import previewIcon from '../../../assets/img/darkify-icon.svg';

export default function Edit({ attributes, setAttributes }) {
	const {
		style,
		size,
		custom_size,
		switch_bg_light,
		switch_bg_dark,
		light_icon_color,
		dark_icon_color,
		light_text_color,
		dark_text_color,
		border_width,
		border_light_color,
		border_dark_color,
		border_radius,
		alignment,
		preview,
	} = attributes;
	const blockProps = useBlockProps();

	const buttonStyle = [
		{ label: 'Classic', value: 'classic' },
		{ label: 'Expand', value: 'expand' },
		{ label: 'Inner Moon', value: 'inner-moon' },
		{ label: 'within', value: 'within' },
		{ label: 'Orbit', value: 'orbit' },
		{ label: 'Around', value: 'around' },
		{ label: 'Dark Side', value: 'dark-side' },
		{ label: 'Horizon', value: 'horizon' },
		{ label: 'Eclipse', value: 'eclipse' },
		{ label: 'Lightbulb', value: 'lightbulb' },
		{ label: 'Dark Inner', value: 'dark-inner' },
		{ label: 'Half Sun', value: 'half-sun' },
		{ label: 'Simple', value: 'simple' },
		{ label: 'Duality', value: 'duality' },
		{ label: 'Dual', value: 'dual' },
		{ label: 'Shift', value: 'shift' },
	];	

	if (preview) {
		return (
			<div {...blockProps}>
				<img
					src={`${previewIcon}`}
					alt="Dark Mode Switcher"
					style={{ width: '100%', height: 'auto' }}
				/>
			</div>
		);
	}

	const ImageSelectControl = ({ options, value, onChange }) => {
		const baseUrl = `${darkify.dir_url}src/Admin/Framework/assets/images/`;
		return (
			<div className="darkify-image-select-control">
				{options.map((opt) => {
					const imageUrl = `${baseUrl}${opt.value}.svg`;
					return (
						<div
							key={opt.value}
							className={`darkify-image-option ${value === opt.value ? 'active' : ''}`}
							onClick={() => onChange(opt.value)}
						>
							<img src={imageUrl} alt={opt.label} />
							<span>{opt.label}</span>
						</div>
					);
				})}
			</div>
		);
	};

	return (
		<div {...useBlockProps()}>
			<BlockControls group="block">
				<AlignmentControl
					value={ alignment }
					onChange={ ( nextAlign ) => {
						setAttributes( { alignment: nextAlign } );
					} }
				/>
			</BlockControls>
			<InspectorControls>
				<PanelBody title={__('Darkify Settings', 'darkify')}>
					<p>{__('Switch Style', 'darkify')}</p>
					<ImageSelectControl
						value={style}
						options={buttonStyle}
						onChange={(val) => setAttributes({ style: val })}
					/>

					<SelectControl
						label={__('Switch Size', 'darkify')}
						value={size}
						options={[
							{ label: 'XS', value: '0.6' },
							{ label: 'S', value: '0.8' },
							{ label: 'M', value: '1.0' },
							{ label: 'L', value: '1.2' },
							{ label: 'XL', value: '1.4' },
							{ label: 'XXL', value: '1.6' },
							{ label: 'Custom', value: 'custom' },
						]}
						onChange={(val) => setAttributes({ size: val })}
					/>

					{size === 'custom' && (
						<RangeControl
							label={__('Custom Size', 'darkify')}
							value={custom_size}
							onChange={(val) =>
								setAttributes({ custom_size: val })
							}
							min={40}
							max={300}
						/>
					)}

					<p>{__('Light Mode Background', 'darkify')}</p>
					<ColorPalette
						value={switch_bg_light}
						onChange={(val) =>
							setAttributes({ switch_bg_light: val })
						}
					/>
					<p>{__('Dark Mode Background', 'darkify')}</p>
					<ColorPalette
						value={switch_bg_dark}
						onChange={(val) =>
							setAttributes({ switch_bg_dark: val })
						}
					/>
					{style !== 'duality' &&
						style !== 'shift' &&
						style !== 'dual' && (
							<>
								<p>
									{__('Light Mode Icon Color', 'darkify')}
								</p>
								<ColorPalette
									value={light_icon_color}
									onChange={(val) =>
										setAttributes({ light_icon_color: val })
									}
								/>
								<p>
									{__('Dark Mode Icon Color', 'darkify')}
								</p>
								<ColorPalette
									value={dark_icon_color}
									onChange={(val) =>
										setAttributes({ dark_icon_color: val })
									}
								/>
							</>
						)}
					{style === 'dual' && (
						<>
							<p>{__('Light Mode Text Color', 'darkify')}</p>
							<ColorPalette
								value={light_text_color}
								onChange={(val) =>
									setAttributes({ light_text_color: val })
								}
							/>
							<p>{__('Dark Mode Text Color', 'darkify')}</p>
							<ColorPalette
								value={dark_text_color}
								onChange={(val) =>
									setAttributes({ dark_text_color: val })
								}
							/>
						</>
					)}

					<RangeControl
						label={__('Border Width', 'darkify')}
						value={border_width}
						onChange={(val) => setAttributes({ border_width: val })}
						min={0}
						max={20}
					/>
					<p>{__('Border Light Color', 'darkify')}</p>
					<ColorPalette
						value={border_light_color}
						onChange={(val) =>
							setAttributes({ border_light_color: val })
						}
					/>
					<p>{__('Border Dark Color', 'darkify')}</p>
					<ColorPalette
						value={border_dark_color}
						onChange={(val) =>
							setAttributes({ border_dark_color: val })
						}
					/>
					<RangeControl
						label={__('Border Radius', 'darkify')}
						value={border_radius}
						onChange={(val) =>
							setAttributes({ border_radius: val })
						}
						min={0}
						max={100}
					/>
				</PanelBody>
			</InspectorControls>

			{/* This renders the actual PHP shortcode output inside the editor */}
			<ServerSideRender
				block="darkify/switcher"
				attributes={attributes}
			/>
		</div>
	);
}
